import { AxiosRequestConfig } from 'axios';
import { Params } from 'react-router-dom';
import {
  DataRoomAnalytics,
  DataRoomMembersWithAccess,
  DataRoomNode,
  DataRoomNodeTag,
  DataRoomNodeVersion,
} from 'router/subrouters/DataRoom/models/DataRoomNode';
import httpClient from '../httpClient';
import {
  BulkAvailabilityMembersResponse,
  BulkAvailabilityUpdateMembers,
} from 'router/subrouters/DataRoom/models/DataRoomBulk';
import {
  UploadDataRoomNode,
  UploadedDataRoomNode,
} from '../../models/DataRoom';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import { getIsNodeFolder } from 'utils';
import {
  DUPLICATE_STATUSES,
  DuplicateStatuses,
  getFileParams,
  uploadFileToUrl,
} from 'uploadFileUtils';

function moveFilesToFolder(dataRoomNodeIds: string[], newParentId: string) {
  return httpClient.post('data-room-nodes/move-file/bulk', {
    newParentId: newParentId || null,
    dataRoomNodeIds,
  });
}

function bulkDeleteNodes(dataRoomNodeIds: string[]) {
  return httpClient.delete('data-room-nodes/bulk', {
    data: { dataRoomNodeIds },
  });
}

function bulkGetManageDetails(fileIds: string[]) {
  return httpClient.get<BulkAvailabilityMembersResponse>(
    'data-room-nodes/documents-availability/bulk',
    {
      params: { nodeIds: fileIds.toLocaleString() },
    },
  );
}

function bulkUpdateManageDetails(
  dataRoomNodeIds: string[],
  data: BulkAvailabilityUpdateMembers,
  parentId: string,
) {
  return httpClient.post<DataRoomNode[]>(
    'data-room-nodes/members-change-availability/bulk',
    {
      dataRoomNodeIds,
      ...(parentId && { parentId }),
      ...data,
    },
  );
}

function getFolderNodes(
  dataRoomId: string,
  parentId?: string,
  excludeFolderIds?: string,
) {
  return httpClient.get<Array<DataRoomNode>>(
    `data-room-nodes/data-rooms/${dataRoomId}/folder-parent-nodes`,
    { params: { parentId, excludeFolderIds } },
  );
}

function getAllParentNodes(
  dataRoomId: string,
  parentId?: string,
  params?: Params,
) {
  return httpClient.get<{
    files: Array<DataRoomNode>;
    folders: Array<DataRoomNode>;
  }>(`data-room-nodes/data-rooms/${dataRoomId}/all-parent-nodes`, {
    params: { ...(parentId ? { parentId } : {}), ...params },
  });
}

function getChildren(dataRoomId: string, parentId?: string) {
  return httpClient.get<Array<DataRoomNode>>(
    `data-room-nodes/data-rooms/${dataRoomId}/parent-nodes`,
    { params: { parentId } },
  );
}

function getChildrenForViewAsMembers(
  dataRoomNodeId: string,
  parentId: string,
  dataRoomMemberId: string,
) {
  return httpClient.get<Array<DataRoomNode>>(
    `data-room-nodes/data-rooms/${dataRoomNodeId}/data-room-members/${dataRoomMemberId}/parent-nodes-buyer-view`,
    { params: { parentId } },
  );
}

function getFolderPath(dataRoomId: string, documentId: string) {
  return httpClient.get<Array<DataRoomNode>>(
    `data-room-nodes/${documentId}/data-rooms/${dataRoomId}/node-path`,
  );
}

function createNode(dataRoomId: string, folderName: string, parentId?: string) {
  return httpClient.post<DataRoomNode>(`data-room-nodes`, {
    folderName,
    dataRoomId,
    parentId,
  });
}

async function uploadDataRoomNode(
  dataRoomId: string,
  file: File,
  parentId?: string,
  conf?: {
    uploadId?: string;
    keepDuplicate?: DuplicateStatuses;
  },
  config?: AxiosRequestConfig,
): Promise<UploadedDataRoomNode | null> {
  const { uploadId, keepDuplicate } = conf;

  const fileParams = getFileParams('dataRoomNode', file);

  const { data } = await httpClient.post<UploadDataRoomNode>(
    `data-room-nodes/data-rooms/${dataRoomId}/upload-file`,
    {
      ...fileParams,
      ...(parentId ? { parentId } : {}),
      ...(uploadId ? { uniqueNodeId: uploadId } : {}),
      ...(keepDuplicate ? { keepDuplicate } : {}),
    },
  );

  const { presignedFile, dataRoomNode, removedDataRoomNodeId } = data;

  const isFolder = getIsNodeFolder(dataRoomNode);

  try {
    await uploadFileToUrl(presignedFile, file, config);
    return { dataRoomNode, removedDataRoomNodeId };
  } catch {
    if (!isFolder) {
      deleteDataRoomNode(dataRoomNode._id);
    }

    return { dataRoomNode: null, removedDataRoomNodeId };
  }
}

function deleteDataRoomNode(nodeId: string) {
  return httpClient.delete<DataRoomNode[]>(`data-room-nodes/${nodeId}`);
}

function patchDataRoomNode(
  nodeId: string,
  dataRoomId: string,
  values: Partial<DataRoomNode>,
) {
  return httpClient.patch<DataRoomNode>(
    `data-room-nodes/${nodeId}/data-rooms/${dataRoomId}`,
    values,
  );
}

function doesNodeExist(
  dataRoomId: string,
  nodeName: string,
  uploadId: string,
  parentId?: string,
) {
  return httpClient.post<{ dataRoomNodeId: string | null }>(
    `data-room-nodes/data-rooms/${dataRoomId}/exist-node`,
    {
      nodeName,
      uniqueNodeId: uploadId,
      parentId,
    },
  );
}

function doesNodesExist(
  dataRoomId: string,
  nodeNames: string[],
  uploadId: string,
  parentId?: string,
) {
  return httpClient.post<Record<string, string>>(
    `data-room-nodes/data-rooms/${dataRoomId}/exist-nodes`,
    {
      nodeNames,
      uniqueNodeId: uploadId,
      parentId,
    },
  );
}

function bulkDownload(
  dataRoomId: string,
  dataRoomNodeIds: string[],
  name: string,
  uniqueId: string,
) {
  return httpClient.post('data-room-nodes/file-buffer-download-bulk', {
    dataRoomId,
    dataRoomNodeIds,
    name,
    uniqueId,
  });
}

function getTaggedNode(dataRoomNodeId: string, tag: DataRoomNodeTag) {
  return httpClient.get<{ dataRoomNode: DataRoomNode | null }>(
    `data-room-nodes/${dataRoomNodeId}/tag/${tag}`,
  );
}

function tagDataRoomNode(dataRoomNodeId: string, tag: DataRoomNodeTag) {
  return httpClient.post<DataRoomNode>(
    `data-room-nodes/${dataRoomNodeId}/tag`,
    {
      tag,
    },
  );
}

function changeIsSecuredDownload(
  dataRoomNodeId: string,
  downloadSecurePdf: boolean,
) {
  return httpClient.post(
    `data-room-nodes/${dataRoomNodeId}/download-secure-pdf`,
    {
      downloadSecurePdf,
    },
  );
}

function getFileHistory(dataRoomNodeId: string, params?: PaginationParams) {
  return httpClient.get<PaginatedData<DataRoomNodeVersion>>(
    `data-room-node-versions/data-room-nodes/${dataRoomNodeId}`,
    {
      params,
    },
  );
}

async function updateNodeVersion(
  id: string,
  file: File & { path?: string; webkitRelativePath?: string },
  uniqueNodeId: string,
  config?: AxiosRequestConfig,
): Promise<UploadedDataRoomNode | null> {
  const fileParams = getFileParams('dataRoomNode', file);

  const { data } = await httpClient.post<
    Omit<UploadDataRoomNode, 'removedDataRoomNodeId'>
  >(`data-room-node-versions/data-room-nodes/${id}`, {
    ...fileParams,
    uniqueNodeId,
    keepDuplicate: DUPLICATE_STATUSES.REPLACE,
  });

  const { presignedFile, dataRoomNode } = data;

  try {
    await uploadFileToUrl(presignedFile, file, config);
    return { dataRoomNode };
  } catch {
    return { dataRoomNode: null };
  }
}

function getFileAnalytics(dataRoomNodeId: string, version: number) {
  return httpClient.get<DataRoomAnalytics[]>(
    `data-room-node-reports/data-room-nodes/${dataRoomNodeId}/version/${version}`,
  );
}

function hideNodeTagBanner(dataRoomNodeId: string) {
  return httpClient.post(`data-room-nodes/${dataRoomNodeId}/deny-tag-nudging`);
}

function getDataRoomNodesForPreview(nodeIds: string[]) {
  return httpClient.get<DataRoomNode[]>(`data-room-nodes/by-ids-info`, {
    params: { nodeIds: nodeIds.join(',') },
  });
}

function getMembersWithAccess(nodeId: string) {
  return httpClient.get<DataRoomMembersWithAccess>(
    `data-room-nodes/${nodeId}/access-members`,
  );
}

function starDocument(dataRoomNodeId: string) {
  return httpClient.post(`data-room-nodes/${dataRoomNodeId}/star`);
}

function unStarDocument(dataRoomNodeId: string) {
  return httpClient.post(`data-room-nodes/${dataRoomNodeId}/unstar`);
}

function getStarredDocuments(dataRoomId: string) {
  return httpClient.get<DataRoomNode[]>(
    `data-room-nodes/data-rooms/${dataRoomId}/starred-files`,
  );
}

function unstarDocuments(dataRoomId: string, dataRoomNodeIds: string[]) {
  return httpClient.post(
    `data-room-nodes/data-rooms/${dataRoomId}/unstar-files`,
    {
      dataRoomNodeIds,
    },
  );
}

export default {
  getChildren,
  createNode,
  getFolderPath,
  uploadDataRoomNode,
  deleteDataRoomNode,
  patchDataRoomNode,
  doesNodeExist,
  doesNodesExist,
  getFolderNodes,
  getAllParentNodes,
  moveFilesToFolder,
  bulkDeleteNodes,
  bulkDownload,
  bulkGetManageDetails,
  bulkUpdateManageDetails,
  getTaggedNode,
  tagDataRoomNode,
  changeIsSecuredDownload,
  getChildrenForViewAsMembers,
  getFileHistory,
  updateNodeVersion,
  getFileAnalytics,
  hideNodeTagBanner,
  getDataRoomNodesForPreview,
  getMembersWithAccess,
  starDocument,
  getStarredDocuments,
  unstarDocuments,
  unStarDocument,
};
